/**
 * 管理者ログイン(docs/10_paltown_platform.md §5「認証: Firebase Auth + admin」)。
 *
 * MVP の方式:
 *   1. **メール+パスワードのみ**。新規登録UIは作らない
 *      (管理者アカウントは Firebase コンソールで手動作成する運用)
 *   2. サインイン後に Firestore `admins/{uid}` を読み、**存在すれば管理者**
 *   3. 無ければ即サインアウトして「権限がありません」を出す
 *
 * TODO(次フェーズ): admin カスタムクレームへ移行する。
 *   Functions で `setCustomUserClaims(uid, { role: 'admin' })` を張り、
 *   ここは `getIdTokenResult()` の claims を見るだけにする。
 *   Firestore ルール側も `request.auth.token.role == 'admin'` に置き換えると
 *   `exists()` の読み取りが不要になり(ルール評価コストも下がる)、
 *   admins コレクションは移行後に廃止できる。
 *   移行までは docs/10 §5 が要求する App Check も未適用(env.ts 参照)。
 */
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  type Auth,
  type User,
} from 'firebase/auth';
import { doc, getDoc, type Firestore } from 'firebase/firestore';

/** 管理者ドキュメントのコレクション名。ルール(firebase/firestore.rules)と揃える。 */
export const ADMINS_COLLECTION = 'admins';

export type AdminAuthErrorCode =
  | 'invalid-credential'
  | 'invalid-email'
  | 'too-many-requests'
  | 'network'
  | 'unsupported'
  | 'not-admin'
  | 'unknown';

export class AdminAuthError extends Error {
  constructor(
    readonly code: AdminAuthErrorCode,
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'AdminAuthError';
  }
}

const MESSAGES: Readonly<Record<AdminAuthErrorCode, string>> = {
  'invalid-credential': 'メールアドレスまたはパスワードが違います。',
  'invalid-email': 'メールアドレスの形式が正しくありません。',
  'too-many-requests': '試行回数が多すぎます。しばらく待ってからやり直してください。',
  network: 'ネットワークに接続できませんでした。',
  unsupported: 'この環境ではログインが利用できません(Auth の設定を確認してください)。',
  'not-admin': '権限がありません。このアカウントは管理者として登録されていません。',
  unknown: 'ログインに失敗しました。',
};

/** 画面にそのまま出せる日本語メッセージ。 */
export function messageFor(code: AdminAuthErrorCode): string {
  return MESSAGES[code];
}

const CODE_MAP: Readonly<Record<string, AdminAuthErrorCode>> = {
  'auth/invalid-credential': 'invalid-credential',
  'auth/wrong-password': 'invalid-credential',
  'auth/user-not-found': 'invalid-credential',
  'auth/user-disabled': 'invalid-credential',
  'auth/invalid-email': 'invalid-email',
  'auth/missing-email': 'invalid-email',
  'auth/missing-password': 'invalid-credential',
  'auth/too-many-requests': 'too-many-requests',
  'auth/network-request-failed': 'network',
  'auth/operation-not-allowed': 'unsupported',
};

function readCode(error: unknown): string {
  if (typeof error === 'object' && error !== null && 'code' in error) {
    const code = (error as { code: unknown }).code;
    if (typeof code === 'string') return code;
  }
  return '';
}

/**
 * SDK のエラーを画面用の `AdminAuthError` に翻訳する。
 *
 * ユーザー列挙を避けるため `user-not-found` も「メールかパスワードが違う」に寄せる
 * (Firebase の既定でも email enumeration protection が有効なら同じ扱いになる)。
 */
export function toAdminAuthError(error: unknown): AdminAuthError {
  if (error instanceof AdminAuthError) return error;
  const code = CODE_MAP[readCode(error)] ?? 'unknown';
  return new AdminAuthError(code, MESSAGES[code], error);
}

export interface AdminSession {
  readonly uid: string;
  readonly email: string | null;
}

export type AdminState =
  | { readonly kind: 'loading' }
  | { readonly kind: 'signedOut'; readonly error: AdminAuthError | null }
  | { readonly kind: 'signedIn'; readonly session: AdminSession };

/** `admins/{uid}` が存在するか。存在=管理者。 */
export async function isAdminUid(db: Firestore, uid: string): Promise<boolean> {
  const snapshot = await getDoc(doc(db, ADMINS_COLLECTION, uid));
  return snapshot.exists();
}

export interface AdminAuthDeps {
  readonly auth: Auth;
  readonly db: Firestore;
}

/**
 * ログイン状態の管理。
 *
 * `onAuthStateChanged` を単一の真実にしている — ページ再読込で復元された
 * セッションも、ログイン直後のセッションも**同じ経路で** admins 判定を通す。
 * (ログイン成功パスだけで判定すると、リロードで判定を飛ばせてしまう)
 */
export class AdminAuthService {
  private state: AdminState = { kind: 'loading' };
  private readonly listeners = new Set<(state: AdminState) => void>();
  /** サインイン時の判定が終わるまで signIn() を待たせるための解決関数。 */
  private pending: ((state: AdminState) => void) | null = null;

  constructor(private readonly deps: AdminAuthDeps) {
    onAuthStateChanged(this.deps.auth, (user) => {
      void this.handleUser(user);
    });
  }

  current(): AdminState {
    return this.state;
  }

  subscribe(listener: (state: AdminState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  /**
   * メール+パスワードでログインし、管理者判定まで終わった状態を返す。
   *
   * 判定は `onAuthStateChanged` 側で走るので、その結果を待ってから返す。
   */
  async signIn(email: string, password: string): Promise<AdminState> {
    const settled = new Promise<AdminState>((resolve) => {
      this.pending = resolve;
    });
    try {
      await signInWithEmailAndPassword(this.deps.auth, email, password);
    } catch (error) {
      this.pending = null;
      const authError = toAdminAuthError(error);
      this.publish({ kind: 'signedOut', error: authError });
      throw authError;
    }
    const state = await settled;
    if (state.kind !== 'signedIn') {
      throw state.kind === 'signedOut' && state.error
        ? state.error
        : new AdminAuthError('unknown', MESSAGES.unknown);
    }
    return state;
  }

  async signOut(): Promise<void> {
    await firebaseSignOut(this.deps.auth);
    this.publish({ kind: 'signedOut', error: null });
  }

  /** 認証状態の変化を1本にまとめて処理する(復元・ログイン・ログアウト共通)。 */
  private async handleUser(user: User | null): Promise<void> {
    if (!user) {
      // signIn() 側で既にエラーを立てている場合はそれを消さない。
      if (this.state.kind !== 'signedOut') this.publish({ kind: 'signedOut', error: null });
      return;
    }
    try {
      if (await isAdminUid(this.deps.db, user.uid)) {
        this.publish({ kind: 'signedIn', session: { uid: user.uid, email: user.email } });
        return;
      }
      // 管理者でないアカウントを掴んだままにしない。
      await firebaseSignOut(this.deps.auth).catch(() => undefined);
      this.publish({
        kind: 'signedOut',
        error: new AdminAuthError('not-admin', MESSAGES['not-admin']),
      });
    } catch (error) {
      // admins/{uid} が読めない = ルール上も管理者ではない、として扱う。
      await firebaseSignOut(this.deps.auth).catch(() => undefined);
      this.publish({
        kind: 'signedOut',
        error: new AdminAuthError('not-admin', MESSAGES['not-admin'], error),
      });
    }
  }

  private publish(state: AdminState): void {
    this.state = state;
    const pending = this.pending;
    this.pending = null;
    pending?.(state);
    for (const listener of this.listeners) listener(state);
  }
}
